/**
 * LPO email generator (clinic orders page). CLIPBOARD feature: the app
 * composes an English note and copies it; nothing here sends anything and
 * nothing touches send-email, Resend or the notification tables.
 *
 * The ORDER LIST the modal shows is NOT fetched here: it comes from the very
 * same orderQueries.ordersListQuery the page already runs, with the page's
 * own scope parameters, so a podiatrist sees exactly the orders the page
 * shows them and an admin sees all, by construction. This module only adds
 * two decorating reads over ids the page has already surfaced, each under
 * its own table's RLS (neither widens what the caller can see):
 *   - lpoLedgerForOrders: which of those orders were already included, by
 *     whom and when (lpo_email_ledger RLS: own clinic or admin)
 *   - patientPrivacyFlags: is_private + patient_code for the patients on
 *     those orders (patients RLS), so private patients render as their code
 * and one write, recordLpoEmailGeneration, called ONLY after the clipboard
 * write resolved. RLS pins generated_by to the caller and refuses drafts,
 * other clinics' orders and spoofed clinic ids; a trigger stamps
 * generated_at.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export interface LpoLedgerEntry {
  order_id: string;
  generation_id: string;
  format_key: string;
  generated_at: string;
  generated_by: string;
  /** Null when the generator's profile is outside the caller's RLS view. */
  generated_by_profile: { full_name: string | null } | null;
}

export interface PatientPrivacyFlag {
  id: string;
  is_private: boolean | null;
  patient_code: string | null;
}

export const lpoEmailKeys = {
  all: ['lpo-email'] as const,
  ledger: (orderIds: string[]) => [...lpoEmailKeys.all, 'ledger', [...orderIds].sort()] as const,
  privacy: (patientIds: string[]) => [...lpoEmailKeys.all, 'privacy', [...patientIds].sort()] as const,
};

/** Ledger rows for the given orders, newest first. */
export function lpoLedgerForOrders(client: SupabaseClient<Database>, orderIds: string[]) {
  return {
    queryKey: lpoEmailKeys.ledger(orderIds),
    queryFn: async (): Promise<LpoLedgerEntry[]> => {
      if (orderIds.length === 0) return [];
      const { data, error } = await client
        .from('lpo_email_ledger')
        .select(
          'order_id, generation_id, format_key, generated_at, generated_by,' +
            ' generated_by_profile:profiles!lpo_email_ledger_generated_by_profile_fkey(full_name)',
        )
        .in('order_id', orderIds)
        .order('generated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as LpoLedgerEntry[];
    },
  };
}

/** Privacy flags for the given patients (the page's list carries only
 *  id + full_name for a patient). */
export function patientPrivacyFlags(client: SupabaseClient<Database>, patientIds: string[]) {
  return {
    queryKey: lpoEmailKeys.privacy(patientIds),
    queryFn: async (): Promise<PatientPrivacyFlag[]> => {
      if (patientIds.length === 0) return [];
      const { data, error } = await client
        .from('patients')
        .select('id, is_private, patient_code')
        .in('id', patientIds);
      if (error) throw error;
      return (data ?? []) as PatientPrivacyFlag[];
    },
  };
}

export interface RecordLpoGenerationInput {
  generationId: string;
  formatKey: string;
  orders: Array<{ id: string; clinic_id: string }>;
}

/** Writes one ledger row per order. The server sets the actor and the
 *  timestamp; the client supplies only the grouping id, format and orders. */
export async function recordLpoEmailGeneration(
  client: SupabaseClient<Database>,
  input: RecordLpoGenerationInput,
): Promise<number> {
  if (input.orders.length === 0) return 0;
  const { data, error } = await client
    .from('lpo_email_ledger')
    .insert(
      input.orders.map((o) => ({
        generation_id: input.generationId,
        format_key: input.formatKey,
        order_id: o.id,
        clinic_id: o.clinic_id,
      })),
    )
    .select('id');
  if (error) throw error;
  return (data ?? []).length;
}
